import { useState } from "react";
import { Gauge, Radar, BatteryCharging, UserCog, Settings } from "lucide-react";
import "./TabVariants.scss";

const TabVariantsDemo = () => {
  const [activeTab, setActiveTab] = useState("dashboard");

  const baseTabs = [
    { to: "/dashboard", label: "Dashboard", icon: Gauge },
    { to: "/search", label: "Search", icon: Radar },
    { to: "/chargers", label: "Chargers", icon: BatteryCharging },
    { to: "/users", label: "Users", icon: UserCog },
    { to: "/settings", label: "Settings", icon: Settings },
  ];

  const variants = [
    {
      name: "Classic Underline Tabs",
      className: "top-tab-bar--underline",
      description: "Clean, minimalist design with animated underline indicator. Good for professional interfaces.",
    },
    {
      name: "Segmented Control Tabs",
      className: "top-tab-bar--segmented",
      description: "Modern segmented control style. Great for iOS-like interfaces with clear visual separation.",
    },
    {
      name: "Minimalist Line Tabs",
      className: "top-tab-bar--minimal",
      description: "Ultra-minimalist with subtle hover effects. Perfect for clean, content-focused layouts.",
    },
    {
      name: "Card Style Tabs",
      className: "top-tab-bar--card",
      description: "Card-based design with borders. Provides clear visual boundaries and good touch targets.",
    },
    {
      name: "Modern Gradient Tabs",
      className: "top-tab-bar--gradient",
      description: "Eye-catching gradient background with white active tabs. Great for modern, vibrant interfaces.",
    },
    {
      name: "Flat Tabs (Non-Button Style)",
      className: "top-tab-bar--flat",
      description: "Clean, traditional tab design without button-like appearance. Perfect for professional interfaces.",
    },
  ];

  return (
    <div style={{ padding: "40px", background: "#f9fafb", minHeight: "100vh" }}>
      <h1 style={{ fontSize: "2rem", fontWeight: "700", marginBottom: "8px", color: "#111827" }}>
        Horizontal Tab Design Variants
      </h1>
      <p style={{ fontSize: "1rem", color: "#6b7280", marginBottom: "48px" }}>
        Explore different tab styles to enhance navigation UX across your application.
      </p>

      {variants.map(variant => (
        <div key={variant.className} style={{ marginBottom: "48px" }}>
          <div style={{ marginBottom: "16px" }}>
            <h2 style={{ fontSize: "1.25rem", fontWeight: "600", color: "#374151", marginBottom: "4px" }}>
              {variant.name}
            </h2>
            <p style={{ fontSize: "0.875rem", color: "#6b7280" }}>{variant.description}</p>
          </div>

          <div className={`top-tab-bar ${variant.className}`}>
            <nav className="top-tab-bar__nav">
              {baseTabs.map(tab => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.to.split("/")[1];

                return (
                  <button
                    key={tab.to}
                    className={`top-tab-bar__tab ${isActive ? "top-tab-bar__tab--active" : ""}`}
                    onClick={() => setActiveTab(tab.to.split("/")[1])}
                  >
                    {Icon && <Icon size={16} className="top-tab-bar__tab-icon" />}
                    <span className="top-tab-bar__tab-label">{tab.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>
        </div>
      ))}

      <div
        style={{
          marginTop: "48px",
          padding: "24px",
          background: "#fff",
          borderRadius: "8px",
          border: "1px solid #e5e7eb",
        }}
      >
        <h3 style={{ fontSize: "1.125rem", fontWeight: "600", color: "#374151", marginBottom: "12px" }}>
          Design Considerations
        </h3>
        <ul style={{ fontSize: "0.875rem", color: "#6b7280", lineHeight: "1.6", paddingLeft: "20px" }}>
          <li>
            <strong>Underline tabs</strong> work well for professional interfaces and follow web conventions
          </li>
          <li>
            <strong>Segmented controls</strong> provide excellent visual feedback and are great for touch interfaces
          </li>
          <li>
            <strong>Minimalist tabs</strong> keep focus on content while maintaining navigation clarity
          </li>
          <li>
            <strong>Card tabs</strong> offer clear visual boundaries and good accessibility
          </li>
          <li>
            <strong>Gradient tabs</strong> add visual interest but may not fit all brand guidelines
          </li>
        </ul>
      </div>
    </div>
  );
};

export default TabVariantsDemo;
